import { Injectable } from '@angular/core';
import { ProductService, CartService, Product } from 'gung-standard';
import { first, map, switchMap } from 'rxjs';
import { forkJoin, of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { getAddonsByParentProduct, flattenArray } from '../utilities/cart-rows-extra-configure-helpers';

// this interface represents a collection of addOns which are associated to a product
// the label is a translated text which is used to label the addon (e.g. in EN: Rim, Assenbly, ... and by default Addon)
export interface AddOnsByProduct {
  productId: string;
  addons: {
    product: Product;
    label: string;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class ColmecAddonsCartService {
  constructor(
    private cartService: CartService,
    private productService: ProductService,
    private translateService: TranslateService
  ) {}

  getAddOnProductsByParentProduct(): Observable<AddOnsByProduct[]> {
    // returns a collection where the parentProductId identifies the product which the collection of addons belong to
    return this.cartService.getCurrentCart().pipe(
      switchMap(cartRows => {
        // get all info under configure
        const addonsByParentProduct = getAddonsByParentProduct(cartRows);

        // flatten array of all addonids
        const allAddonIds = flattenArray(addonsByParentProduct.map(x => x.addonsIds));

        return forkJoin([
          of(cartRows.map(row => row.productId)),
          of(addonsByParentProduct),
          this.productService.getFullProductsByIds(allAddonIds).pipe(first())
        ]);
      }),
      map(([cartRowProductIds, addonsByParentProduct, retrivedAddonProducts]) => {
        const mapedAddOnProducts: AddOnsByProduct[] = [];

        // go through all cart row products
        // if any contains an addon then add the addon info
        for (const cartRowProductId of cartRowProductIds) {
          const index = addonsByParentProduct.findIndex(x => x.parentProductId === cartRowProductId);
          if (index >= 0) {
            const addonsIds = addonsByParentProduct[index].addonsIds;
            const addonsKeys = addonsByParentProduct[index].addonsKeys;

            // go through all the addons for the current product in the current cartRow and build object to return
            const addOnsByProduct: AddOnsByProduct = {
              productId: cartRowProductId,
              addons: []
            };

            let position = 0;
            for (const addonsKey of addonsKeys) {
              addOnsByProduct.addons.push({
                product: retrivedAddonProducts.find(p => p.id === addonsIds[position]),
                label: this.getTranslatedLabelByAddonKey(addonsKey)
              });

              position++;
            }

            mapedAddOnProducts.push(addOnsByProduct);
          }
        }

        return mapedAddOnProducts;
      })
    );
  }

  private getTranslatedLabelByAddonKey(addonKey): string {
    if (addonKey === 'rim') {
      return this.translateService.instant('COLMEC_RIM');
    }

    if (addonKey === 'assembly') {
      return this.translateService.instant('COLMEC_ASSEMBLY');
    }

    if (addonKey === 'autoAdded') {
      return this.translateService.instant('COLMEC_AUTO_ADDED');
    }

    return this.translateService.instant('COLMEC_ADDON');
  }
}
